import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, TablePagination, Chip, LinearProgress
} from "@mui/material";
import { ThumbUp, ThumbDown, Comment, Visibility, Download } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { API_ORIGIN as API_URL } from "../../config/env";
import DateRangeFilter, { buildDateRangeParams, defaultDateRangeFilter } from "../../components/common/DateRangeFilter";
import { AdminAction } from "../../components/admin";

export default function EngagementDashboard() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(defaultDateRangeFilter);

  const token = localStorage.getItem("rfis");

  const openVideoComments = (video) => {
    navigate(`/comments/${video.id}`, {
      state: {
        videoTitle: video.title,
        videoLanguage: video.language,
        videoCategory: video.category,
      },
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/v1/video-interactions/admin/engagement`, {
        headers: { rf_token_header123_k: token },
        params: { page: page + 1, limit: rowsPerPage, ...buildDateRangeParams(dateFilter) },
      });
      setVideos(res.data.data.videos);
      setTotal(res.data.data.total);
    } catch (err) {
      toast.error("Failed to fetch engagement data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, dateFilter]);

  const handleExport = () => {
    // Create CSV from current data
    const headers = "ID,Title,Category,Language,Views,Likes,Dislikes,Comments\n";
    const csv = videos.map((v) =>
      `${v.id},"${v.title}",${v.category},${v.language},${v.views || 0},${v.likes || 0},${v.dislikes || 0},${v.comments || 0}`
    ).join("\n");

    const blob = new Blob([headers + csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "engagement_report.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported!");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h5" fontWeight="bold">
          Engagement Dashboard
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <DateRangeFilter value={dateFilter} onChange={setDateFilter} />
          <AdminAction variant="outline" startIcon={<Download />} onClick={handleExport}>
            Export CSV
          </AdminAction>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Likes, dislikes, and comments per video. Sorted by views.
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Title</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Language</strong></TableCell>
              <TableCell align="center">
                <Visibility fontSize="small" /> Views
              </TableCell>
              <TableCell align="center">
                <ThumbUp fontSize="small" color="success" /> Likes
              </TableCell>
              <TableCell align="center">
                <ThumbDown fontSize="small" color="error" /> Dislikes
              </TableCell>
              <TableCell align="center">
                <Comment fontSize="small" color="info" /> Comments
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {videos.map((video) => (
              <TableRow key={video.id}>
                <TableCell>{video.id}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {video.title}
                </TableCell>
                <TableCell>
                  <Chip label={video.category || "N/A"} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{video.language || "N/A"}</TableCell>
                <TableCell align="center">{video.views || 0}</TableCell>
                <TableCell align="center" sx={{ color: "green" }}>{video.likes || 0}</TableCell>
                <TableCell align="center" sx={{ color: "red" }}>{video.dislikes || 0}</TableCell>
                <TableCell align="center">
                  <button
                    type="button"
                    onClick={() => openVideoComments(video)}
                    title="View comments"
                    aria-label={`View comments for ${video.title || `video ${video.id}`}`}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      margin: 0,
                      cursor: "pointer",
                      font: "inherit",
                      fontWeight: 700,
                      color: "#2563eb",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                    }}
                  >
                    {video.comments || 0}
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        />
      </TableContainer>
    </Box>
  );
}
